import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../QuizExam/Quiz.css';
import ExamImage from '../../../../../assets/images/examimage.jpg';
import { fetchExamById } from '../../../../../api/examApi';
import { startExam, getNextQuestion } from '../../../../../api/questionExamApi';
import authService from '../../../../../services/authService';
import { fetchExamResults } from '../../../../../api/quizResultApi';

function ExamQuiz() {
    const [isExamStarted, setIsExamStarted] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [timer, setTimer] = useState(3600);
    const [selectedAnswers, setSelectedAnswers] = useState([]);
    const [isExamFinished, setIsExamFinished] = useState(false);
    const [userExamId, setUserExamId] = useState(null);
    const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [isLastQuestion, setIsLastQuestion] = useState(false);
    const [nextQuestion, setNextQuestion] = useState(null);
    const [isStarting, setIsStarting] = useState(false);
    const [trainingRequired, setTrainingRequired] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');

    const { examId } = useParams();
    const [examDetails, setExamDetails] = useState({
        examName: '',
        numberOfQuestions: 0,
        durationMinutes: 60
    });

    const navigate = useNavigate();

    useEffect(() => {
        const loadExamDetails = async () => {
            try {
                const examData = await fetchExamById(examId);
                setExamDetails({
                    examName: examData.examName,
                    numberOfQuestions: examData.numberOfQuestions,
                    durationMinutes: examData.durationMinutes
                });
                setTotalQuestions(examData.numberOfQuestions);
                setTimer(examData.durationMinutes * 60);
            } catch (error) {
                // Handle error
            }
        };

        loadExamDetails();
    }, [examId]);

    const handleStartExam = async () => {
        setIsStarting(true);
        try {
            const userId = authService.getUserId();
            if (!userId) {
                return;
            }

            const response = await startExam(examId, userId);
            
            // Check if training is required based on statusCode
            if (response.statusCode === 1) {
                setTrainingRequired(true);
                setResponseMessage(response?.responseMsg);
                setIsStarting(false);
                return;
            }
            
            setUserExamId(response.userExamId);
            const newQuestion = {
                id: response.nextQId,
                description: response.nextQDesc,
                options: response.options
            };
            setCurrentQuestion(newQuestion);
            setIsExamStarted(true);
            setCurrentQuestionIndex(1);
        } catch (error) {
            // Handle error
        } finally {
            setIsStarting(false);
        }
    };

    const handleSubmit = async () => {
        if (selectedAnswers.length === 0) {
            alert("Please select at least one answer before submitting.");
            return;
        }

        try {
            const response = await getNextQuestion({
                userExamId: userExamId,
                previousQId: currentQuestion.id,
                previousQAns: selectedAnswers.join(", "),
                previousQExp: null,
                previousQSelectedAns: selectedAnswers.join(", "),
                nextQId: 0,
                nextQDesc: "",
                options: []
            });

            setIsAnswerSubmitted(true);

            if (response.nextQId === null) {
                setIsLastQuestion(true);
            } else {
                const newQuestion = {
                    id: response.nextQId,
                    description: response.nextQDesc,
                    options: response.options
                };
                setNextQuestion(newQuestion);
            }
        } catch (error) {
            // Handle error
        }
    };

    const handleNext = () => {
        if (isAnswerSubmitted) {
            setCurrentQuestion(nextQuestion);
            setNextQuestion(null);
            setSelectedAnswers([]);
            setIsAnswerSubmitted(false);
            setCurrentQuestionIndex(prevIndex => prevIndex + 1);
        }
    };

    const handleFinalSubmit = async () => {
        try {
            await getNextQuestion({
                userExamId: userExamId,
                previousQId: currentQuestion.id,
                previousQAns: selectedAnswers.join(", "),
                previousQExp: null,
                previousQSelectedAns: selectedAnswers.join(", "),
                nextQId: null,
                nextQDesc: null,
                options: null,
                finalSubmit: 1
            });

            const userEmail = authService.getUserEmail();
            if (!userEmail) {
                throw new Error('User email not found');
            }
            
            const results = await fetchExamResults(userEmail);
            const userResult = results.find(exam => exam.userExamId === parseInt(userExamId));

            if (userResult) {
                setIsExamFinished(true);
                navigate('/user/results', { 
                    state: { 
                        userExamId: userExamId,
                        examResult: userResult
                    } 
                });
            }
        } catch (error) {
            // Handle error
        }
    };

    const handleOptionChange = (optionSrNo) => {
        setSelectedAnswers(prev => {
            if (prev.includes(optionSrNo)) {
                return prev.filter(item => item !== optionSrNo);
            } else {
                return [...prev, optionSrNo];
            }
        });
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    useEffect(() => {
        let interval = null;

        if (isExamStarted && !isExamFinished) {
            interval = setInterval(() => {
                setTimer(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        setIsExamFinished(true);
                        navigate('/results', { state: { examId: examId, userExamId: userExamId } });
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [isExamStarted, isExamFinished, navigate, examId, userExamId]);

    if (!isExamStarted) {
        return (
            <div className="start-screen">
                <div className="exam-banner">
                    <img src={ExamImage} alt="Exam banner" className='exambanner' />
                    <div className="exam-details">
                        <h2>{examDetails.examName}</h2>
                        <div className="exam-details-information">
                            <div className="detail-information">
                                <span className="label-information">Duration (mins):</span>
                                <span className="value">{examDetails.durationMinutes}</span>
                            </div>
                            <div className="detail-information">
                                <span className="label-information">Questions:</span>
                                <span className="value">{examDetails.numberOfQuestions}</span>
                            </div>
                        </div>
                        <p><strong>Note:</strong> Once your exam has started, please remain in the session until you have completed it.</p>
                        {trainingRequired && (
                            <>
                            <br />
                            <div className="training-message" style={{ color: 'red', marginBottom: '15px', fontWeight: '500', fontSize: '14px' }}>
                                {responseMessage}
                            </div>
                            </>
                        )}
                        <button 
                            className="start-exam-button" 
                            onClick={handleStartExam}
                            disabled={isStarting || trainingRequired}
                            style={{ 
                                backgroundColor: (isStarting || trainingRequired) ? '#cccccc' : '#2c3b97',
                                cursor: (isStarting || trainingRequired) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isStarting ? 'Starting Exam...' : 'Start Exam'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!currentQuestion) {
        return <div>Loading question...</div>;
    }

    return (
        <div className="quiz-exam">
            <div className='timing-for'>
                <h2 className='mock-practice-h2'>{examDetails.examName}</h2>
                <div>
                    <span className='time-remaining'>Time Remaining:</span>
                    <span className="timer" style={{ backgroundColor: timer < 60 ? 'red' : '#2c3b97' }}>
                        {formatTime(timer)} Mins
                    </span>
                </div>
            </div>

            <div className="question-section">
                <div className="progress-bar-container-quiz">
                    <div className="progress-bar-quiz" style={{ width: `${(currentQuestionIndex / totalQuestions) * 100}%` }}></div>
                </div>

                <h3 className='quiz-exam-question-h3'>Question {currentQuestionIndex}/{totalQuestions}:</h3>
                <p className='question-Heading'>{currentQuestion.description}</p>
                <p className='appropriate-option'>
                    Choose the Most Appropriate Options. 
                </p>

                <div className="options">
                    {currentQuestion.options.map((option) => (
                        <div key={option.optionSrNo} className="option-container">
                            <input
                                type="checkbox"
                                name="question"
                                id={option.optionSrNo}
                                value={option.optionSrNo}
                                checked={selectedAnswers.includes(option.optionSrNo)}
                                onChange={() => handleOptionChange(option.optionSrNo)}
                                className='quiz-options'
                                disabled={isAnswerSubmitted}
                            />
                            <label htmlFor={option.optionSrNo} className="option-label">
                                <span className="option-text">
                                    {option.optionText}
                                </span>
                            </label>
                        </div>
                    ))}
                </div>

                <div className="navigation-buttons">
                    {!isAnswerSubmitted ? (
                        <button
                            className="navigation-submit-button"
                            onClick={handleSubmit}
                            disabled={selectedAnswers.length === 0}
                        >
                            Submit
                        </button>
                    ) : isLastQuestion ? (
                        <button
                            className="navigation-final-submit-button"
                            onClick={handleFinalSubmit}
                        >
                            Final Submit
                        </button>
                    ) : (
                        <button
                            className="navigation-next-button"
                            onClick={handleNext}
                        >
                            Next
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ExamQuiz;